'use client';

import { useState } from 'react';
import { PartyPopper, ChevronRight } from 'lucide-react';

interface TemplatesCompleteModalProps {
  onFinish: () => void;
}

/**
 * Completion modal shown at the end of the Templates Tour.
 * Two-screen carousel celebrating completion and encouraging custom templates.
 */
export function TemplatesCompleteModal({ onFinish }: TemplatesCompleteModalProps) {
  const [currentScreen, setCurrentScreen] = useState(0);
  const isLastScreen = currentScreen === 1;

  const handleNext = () => {
    if (isLastScreen) {
      onFinish();
    } else {
      setCurrentScreen(1);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Templates tour complete"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8">
        {/* Icon */}
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-accent-blue/[0.08]">
          <PartyPopper className="h-8 w-8 text-accent-blue" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-900">Templates Tour Complete!</h2>

        {/* Content */}
        {currentScreen === 0 && (
          <div className="text-left space-y-4 mb-8">
            <p className="text-base leading-relaxed text-gray-500">
              You now know how to quickly insert pre-written templates into your messages. Templates save time and keep
              your agency&rsquo;s communication consistent.
            </p>
          </div>
        )}

        {currentScreen === 1 && (
          <div className="text-left space-y-4 mb-8">
            <p className="text-base leading-relaxed text-gray-500">
              Now let&rsquo;s get started on building your own! Head to Settings to create custom templates tailored to
              your agency&rsquo;s needs.
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-center">
          <button
            onClick={handleNext}
            aria-label={isLastScreen ? 'Finish tour' : 'Next screen'}
            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full text-base font-semibold text-white bg-accent-blue hover:brightness-110 transition-all shadow-md"
          >
            {isLastScreen ? (
              'Finish Tour'
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-1.5 mt-5">
          {[0, 1].map((i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === currentScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
